import React, { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'

const HELP_ENTRIES = [
  {
    keywords: ['upload', 'hochladen', 'hochlade', 'datei', 'wav', 'flac', 'track hoch'],
    answer: 'Einen Track lädst du so hoch: Gehe zu **Upload** (Navigation) und wähle eine Audio-Datei (WAV oder FLAC). Nach dem Upload erscheint der Track im Dashboard und wird automatisch analysiert und gemastert.',
  },
  {
    keywords: ['mastering', 'master', 'lautstärke', 'lufs', 'qualität'],
    answer: 'Das Mastering läuft automatisch nach dem Upload. Du siehst den Fortschritt im **Dashboard** und unter **Jobs**. Fertige Masters findest du unter **Masters** (History). Auf der Track-Detailseite kannst du WAV und MP3 herunterladen und bei Bedarf „Mastering erneut ausführen“ wählen.',
  },
  {
    keywords: ['cover', 'coverbild', 'artwork', 'bild'],
    answer: '**Cover generieren**: Auf der Track-Detailseite (nach dem Mastering) findest du „Cover generieren“. DALL-E erzeugt das komplette Cover – optional inkl. Titel und Künstler im Bild. Optional eigener Design-Prompt (z.B. Stil, Farben). Cover als PNG, wird ins MP3 eingebettet.',
  },
  {
    keywords: ['canvas', 'spotify', 'video', 'vertikal', '9:16'],
    answer: '**Spotify-Canvas**: Ein vertikales Bild oder kurzes Video (9:16), passend zu Titel und Song – z.B. für Spotify. Auf der Track-Detailseite „Canvas generieren“ wählen, dann **Bild (PNG)** oder **Video (MP4)** und optional einen Design-Prompt. Das Ergebnis kannst du herunterladen und z.B. bei Spotify als Canvas nutzen.',
  },
  {
    keywords: ['metadaten', 'titel', 'künstler', 'artist', 'genre', 'album'],
    answer: 'Metadaten (Titel, Künstler, Album, Genre, Jahr usw.) bearbeitest du auf der **Track-Detailseite** im Bereich Metadaten. Dort gibt es auch „Metadaten vorschlagen“, damit die KI Vorschläge aus dem Dateinamen oder der Analyse macht.',
  },
  {
    keywords: ['anleitung', 'hilfe', 'wie geht', 'erste schritte'],
    answer: 'Eine Schritt-für-Schritt-Anleitung findest du unter **Anleitung** (Link im Footer). Kurz: Upload → warten auf Analyse & Mastering → Track öffnen → WAV/MP3/Cover/Canvas herunterladen.',
  },
  {
    keywords: ['faq', 'fragen', 'häufig'],
    answer: 'Häufige Fragen und Antworten stehen auf der **FAQ**-Seite (Link im Footer). Dort findest du z.B. Infos zu Formaten, Cover, Canvas und Anbieter.',
  },
  {
    keywords: ['impressum', 'anbieter', 'recht', 'kontakt', 'dj mac', 'marc fischer'],
    answer: 'Impressum und Anbieterinfos findest du unter **Impressum** im Footer. Anwendung von anwendungen.online – © DJ Mac (Marc Fischer). Von Musiker für Musiker.',
  },
  {
    keywords: ['download', 'herunterladen', 'wav', 'mp3', 'export'],
    answer: 'Nach dem Mastering erscheinen auf der Track-Detailseite die Download-Buttons: **WAV**, **MP3**, optional **Report** (TXT) und **Cover** (PNG). Wenn du ein Canvas erzeugt hast, gibt es auch einen Canvas-Download (PNG oder MP4). Das **Azuracast-ZIP** enthält MP3 + Cover + Metadaten für den Einsatz in Azuracast.',
  },
  {
    keywords: ['vergleich', 'compare', 'a/b', 'vorher nachher'],
    answer: 'Den **A/B-Vergleich** (Original vs. Master) findest du auf der Track-Detailseite im Bereich „A/B-Vergleich“. Du kannst auch die Seite **Vergleichen** in der Navigation nutzen, um zwei Tracks nebeneinander zu vergleichen.',
  },
  {
    keywords: ['jobs', 'warteschlange', 'queue', 'status', 'läuft'],
    answer: 'Unter **Jobs** siehst du die Warteschlange und den Status laufender Aufgaben (Analyse, Mastering, Cover, Canvas). Wenn ein Job „running“ ist, wird die Track-Detailseite automatisch aktualisiert.',
  },
]

const FALLBACK_ANSWER = 'Zu deiner Frage habe ich nichts Passendes gefunden. Du kannst z.B. fragen: Wie lade ich einen Track hoch? Was ist ein Spotify-Canvas? Wie funktioniert das Cover? Oder schau in die **Anleitung** und **FAQ** (Links im Footer).'

function findAnswer(userText) {
  const normalized = userText.toLowerCase().trim().replace(/\s+/g, ' ')
  if (!normalized) return null
  for (const entry of HELP_ENTRIES) {
    for (const kw of entry.keywords) {
      if (normalized.includes(kw.toLowerCase()) || kw.toLowerCase().includes(normalized)) {
        return entry.answer
      }
    }
  }
  return null
}

function formatMessage(text) {
  return text.split(/\*\*(.*?)\*\*/g).map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part))
}

export default function ChatBot() {
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  useEffect(() => { scrollToBottom() }, [messages])

  useEffect(() => {
    if (open) {
      inputRef.current?.focus()
      if (messages.length === 0) {
        setMessages([
          {
            id: 'welcome',
            role: 'bot',
            text: 'Hallo! Ich bin dein Helfer für das AI Mastering Studio. Stell mir eine Frage, z.B. wie du einen Track hochlädst, wie Cover oder Canvas funktionieren, oder wo du die Anleitung findest.',
          },
        ])
      }
    }
  }, [open])

  const sendWithText = (textToSend) => {
    const text = (typeof textToSend === 'string' ? textToSend : input).trim()
    if (!text || loading) return
    setInput('')
    const userMsg = { id: Date.now().toString(), role: 'user', text }
    setMessages((m) => [...m, userMsg])
    setLoading(true)
    setTimeout(() => {
      const answer = findAnswer(text) || FALLBACK_ANSWER
      setMessages((m) => [...m, { id: (Date.now() + 1).toString(), role: 'bot', text: answer }])
      setLoading(false)
    }, 400)
  }

  const sendMessage = () => sendWithText(input)

  const suggestions = [
    'Wie lade ich einen Track hoch?',
    'Was ist ein Spotify-Canvas?',
    'Wie funktioniert das Cover?',
  ]

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-20 right-4 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-violet-500 text-white shadow-lg transition hover:bg-violet-600 md:bottom-6"
        aria-label="Hilfe-Chat öffnen"
      >
        ?
      </button>

      {open && (
        <div className="fixed inset-0 z-[100] flex flex-col bg-black/40 md:inset-auto md:bottom-6 md:right-6 md:top-auto md:h-[420px] md:w-[380px] md:rounded-2xl md:shadow-2xl">
          <div
            className="flex flex-1 flex-col overflow-hidden rounded-t-2xl border border-white/10 bg-studio-bg backdrop-blur-xl md:rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <h2 className="text-sm font-semibold text-white">Hilfe – AI Mastering Studio</h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded p-1 text-white/60 hover:bg-white/10 hover:text-white"
                aria-label="Schließen"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${
                      msg.role === 'user'
                        ? 'bg-violet-500/80 text-white'
                        : 'bg-white/10 text-white/90'
                    }`}
                  >
                    {msg.role === 'user' ? msg.text : formatMessage(msg.text)}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="rounded-2xl bg-white/10 px-4 py-2.5 text-sm text-white/70">
                    …
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            {messages.length <= 1 && (
              <div className="flex flex-wrap gap-2 px-4 pb-2">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => sendWithText(s)}
                    className="rounded-full border border-white/20 bg-white/5 px-3 py-1.5 text-xs text-white/80 hover:bg-white/10"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            <div className="flex gap-2 border-t border-white/10 p-3">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                placeholder="Frage eingeben…"
                className="flex-1 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder-white/40 focus:border-violet-500/50 focus:outline-none focus:ring-1 focus:ring-violet-500/50"
              />
              <button
                type="button"
                onClick={sendMessage}
                disabled={loading || !input.trim()}
                className="rounded-xl bg-violet-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-violet-600 disabled:opacity-50"
              >
                Senden
              </button>
            </div>
            <div className="border-t border-white/10 px-4 py-2 text-center">
              <Link to="/anleitung" className="text-xs text-violet-300 hover:underline" onClick={() => setOpen(false)}>
                Anleitung
              </Link>
              {' · '}
              <Link to="/faq" className="text-xs text-violet-300 hover:underline" onClick={() => setOpen(false)}>
                FAQ
              </Link>
            </div>
          </div>
        </div>
      )}

      {open && (
        <button
          type="button"
          className="fixed inset-0 z-[99] md:hidden"
          aria-label="Overlay schließen"
          onClick={() => setOpen(false)}
        />
      )}
    </>
  )
}
